import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import type { DiscoveryPromptDoneIntent } from "../types/test-agent-2.ts";
import { humanizeText } from "../utils/text.ts";
import { MarkdownBlock } from "./markdown-block.tsx";

type Props = {
  doneIntent: DiscoveryPromptDoneIntent;
};

export const DiscoveryPromptDetails = ({ doneIntent }: Props) => {
  return (
    <Stack spacing={2}>
      {/* Header with key metrics */}
      <Stack direction="row" spacing={1} flexWrap="wrap">
        <Chip label={`${doneIntent.test_count} tests`} color="primary" size="small" />
        <Chip
          label={doneIntent.variant}
          color={doneIntent.variant === "edge_case" ? "warning" : "default"}
          size="small"
          variant="outlined"
        />
        <Chip
          label={doneIntent.status}
          color={doneIntent.status === "success" ? "success" : "error"}
          size="small"
          variant="outlined"
        />
      </Stack>

      {/* Intent */}
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Discovery Intent
          </Typography>
          <Box
            sx={{
              maxHeight: "500px",
              overflowY: "auto",
            }}
          >
            <MarkdownBlock content={doneIntent.intent} />
          </Box>
        </CardContent>
      </Card>

      {/* Intent Rationale */}
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Rationale
          </Typography>
          <Typography variant="body2">{doneIntent.intent_rationale}</Typography>
        </CardContent>
      </Card>

      {/* References */}
      {doneIntent.reference_ids && doneIntent.reference_ids.length > 0 && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Knowledge References ({doneIntent.reference_ids.length})
            </Typography>
            <Divider sx={{ my: 1 }} />
            <List dense disablePadding>
              {doneIntent.reference_ids.map((ref, index) => (
                <ListItem key={index} disablePadding sx={{ py: 0.5 }}>
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                        <Chip
                          label={humanizeText(ref.entity_type)}
                          size="small"
                          variant="outlined"
                        />
                        <Typography variant="caption" color="text.secondary">
                          {ref.entity_id}
                        </Typography>
                      </Stack>
                    }
                    secondary={ref.summary}
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* Additional Info */}
      <Stack direction="row" spacing={2} flexWrap="wrap">
        {doneIntent.persona_id && (
          <Typography variant="caption" color="text.secondary">
            Persona: {doneIntent.persona_id.slice(0, 8)}...
          </Typography>
        )}
        {doneIntent.test_dependency_id && (
          <Typography variant="caption" color="text.secondary">
            Dependency: {doneIntent.test_dependency_id.slice(0, 8)}...
          </Typography>
        )}
      </Stack>
    </Stack>
  );
};

export default DiscoveryPromptDetails;
